import { DynamicCalltrackingButton, EPageTypes, EPlatformTypes } from '@cian/frontend-dynamic-calltracking-component';
import classNames from 'classnames';
import { once } from 'ramda';
import * as React from 'react';

import { IFavoriteButtonContext } from 'shared/context/favoritesButton';
import {
  IAgentSchema,
  IOfferSchema,
  IPhoneSchema,
  IUserSchema,
} from 'shared/types/api-models/offer-card/v1/get-offer-data';

import * as styles from './index.css';
import { trackShowPhones } from './tracking';
import { FavoriteButtonContainer } from '../../../../containers/FavoriteButtonContainer';
import { FavoritesButtonContext } from '../../../../context';
import { getCommercialAreaForMultiAds, getOfferTitle, getSquareInformation } from '../../../../utils/offer_helpers';
import { formatNumber } from '../../../../utils/phone';

export interface IFullScreenControlsCommonProps {
  abGroup: number;
  agent: IAgentSchema | null;
  dynamicCalltrackingPlaceType: string;
  isAuthenticated: boolean;
  kpId: number | null;
  newbuildingId: number | null;
  offer: IOfferSchema;
  price: string | null;
  siteBlockId: number | null;
  user: IUserSchema | null;
  isOfferMultiAds: boolean;
  callButtonText: string;
}

export interface IFullScreenControlsOwnProps {
  hidePhoneButton?: boolean;
  hideFavoritesButton?: boolean;
  nextSliderCompat?: boolean;
  exitFullscreen?(): void;
}

export interface IFullScreenControlsDispatchProps {
  setPhoneCollapse(): void;
}

export interface IFullscreenControlsState {
  showPhones: boolean;
}

export type IFullScreenControlsProps = IFullScreenControlsCommonProps &
  IFullScreenControlsDispatchProps &
  IFullScreenControlsOwnProps;

export class FullScreenControls extends React.Component<IFullScreenControlsProps, IFullscreenControlsState> {
  public constructor(props: IFullScreenControlsProps) {
    super(props);

    this.state = {
      showPhones: false,
    };
  }

  public render(): React.ReactNode {
    const { showPhones } = this.state;
    const { offer, siteBlockId, dynamicCalltrackingPlaceType, hidePhoneButton, hideFavoritesButton, callButtonText } =
      this.props;
    const { phones } = offer;

    const phonesAvailable = !hidePhoneButton && !!phones && !phones.some(phone => !phone.countryCode || !phone.number);
    const formattedPhones = phonesAvailable ? (phones as IPhoneSchema[]).map(formatNumber).join(', ') : '';

    return (
      <div
        className={classNames(styles['controls'], { [styles['controls--next-compat']]: this.props.nextSliderCompat })}
      >
        <div className={styles['title']}>{this.getTitle()}</div>

        {phonesAvailable && (
          <DynamicCalltrackingButton
            announcementId={offer.id}
            blockId={siteBlockId}
            className={styles['show-phone']}
            isOpen={showPhones}
            onClick={this.showPhones}
            openPhoneClassName={styles['phone']}
            pageType={EPageTypes.OfferCard}
            phone={formattedPhones}
            placeType={dynamicCalltrackingPlaceType}
            platformType={EPlatformTypes.WebDesktop}
          >
            {callButtonText}
          </DynamicCalltrackingButton>
        )}

        {!hideFavoritesButton && (
          <div className={styles['favorites-wrapper']}>
            <FavoritesButtonContext.Provider value={this.getCustomFavoriteContext()}>
              <FavoriteButtonContainer />
            </FavoritesButtonContext.Provider>
          </div>
        )}

        {this.props.exitFullscreen && <div className={styles['close']} onClick={this.props.exitFullscreen} />}
      </div>
    );
  }

  private getCustomFavoriteContext = once(
    (): IFavoriteButtonContext => ({
      customClass: styles['favorites'],
      placement: 'gallery',
      popupPivotX: 'right' as const,
    }),
  );

  private getTitle(): string {
    const { offer, price, isOfferMultiAds } = this.props;

    const squareInformation = isOfferMultiAds
      ? getCommercialAreaForMultiAds(offer.minArea, offer.maxArea)
      : getSquareInformation(offer);

    return [getOfferTitle(offer), squareInformation, price].filter(Boolean).join(', ').trim();
  }

  private readonly showPhones = (): void => {
    const { abGroup, agent, kpId, offer, setPhoneCollapse, siteBlockId, user } = this.props;

    trackShowPhones(offer, agent, user, abGroup, kpId, siteBlockId);

    setPhoneCollapse();
    this.setState({ showPhones: true });
  };
}
